import { Navigate, useParams, useNavigate } from "react-router-dom";
import { BreadCrumbs } from "@/shared/components/ui/BreadCrumb/BreadCrumbs";
import UnderlineTabs from "@/shared/components/ui/Tabs/UnderlineTabs";
import { CampaignTabs } from "@/shared/utils/utils";
import AnalyticsContent from "./Analytics/AnalyticsContent";
import StepsContent from "./StepsContent";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { CalendarClock, Copy, Pause, Play, Settings, Trash } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import CampaignLeadsContent from "./CampaignLeadsContent";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import SettingContent from "./SettingContent";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import {
  CAMPAIGN_HTTP_ACTION_KEY,
  useCampaignStatusAction,
  useDuplicateCampaign,
  useDeleteCampaign,
  useFetchCampaignLeads,
  useGetCampaignById,
  useGetCampaignReadiness,
} from "@/features/campaigns/hooks/useCampaign";
import Loader from "@/shared/components/ui/Loaders/Loader";
import IndividualSequenceContent from "./IndividualSequence/IndividualSequenceContent";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import { showError } from "@/shared/utils/toast";
import CampaignStartConfirmationModal from "./components/campaignStartConfirmationModal";
import { getCampaignScheduledRunState } from "../../utils/campaignScheduleDisplay";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";

/** HTTP action for POST /campaign/:id/:action from current campaign status (explicit mutate payload). */
const resolveActionForStatus = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s === "draft") return "start";
  if (s === "paused") return "resume";
  if (s === "active" || s === "running") return "stop";
  if (s === "scheduled") return null;
  return "start";
};

export default function CampaignDetails() {
  const { campaignId: campaignIdFromParams, tab } = useParams();
  const navigate = useNavigate();
  const [campaignStatus, setCampaignStatus] = useState(undefined);
  const campaignId = useCampaignId() || campaignIdFromParams;
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign({
    action: () => {
      setShowDeleteConfirmModal(false);
    },
  });
  const { mutate: duplicateCampaign } = useDuplicateCampaign({
    action: (data) => {
      const duplicated = data?.data ?? data;
      if (duplicated?.id) {
        navigate(`/campaigns/${duplicated.id}/campaignsteps`);
      }
    },
  });

  const { data: campaignData, isLoading: isLoadingCampaign } =
    useGetCampaignById(campaignId);

  const { data: leadsCountData, isLoading: isLeadsCountLoading } =
    useFetchCampaignLeads({
      campaignId,
      page: 1,
      limit: 1,
      enabled: !!campaignId,
    });
  const leadsTotal = leadsCountData?.total ?? 0;
  const hasLeads = leadsTotal > 0;

  const normalizedCampaign = useMemo(() => {
    if (campaignData == null) return null;
    const raw =
      campaignData?.data != null && typeof campaignData.data === "object"
        ? campaignData.data
        : campaignData;
    const schedule = raw?.start_schedule ?? raw?.startSchedule;
    if (schedule && !raw?.start_schedule) {
      return { ...raw, start_schedule: schedule };
    }
    return raw;
  }, [campaignData]);

  const statusFromApi = normalizedCampaign?.status
    ? String(normalizedCampaign.status).toLowerCase()
    : undefined;

  const scheduledRun = useMemo(
    () => getCampaignScheduledRunState(normalizedCampaign),
    [normalizedCampaign],
  );

  /** Includes derived `scheduled` when API is active + future schedule_later (or status scheduled). */
  const effectiveCampaignStatus = useMemo(() => {
    if (scheduledRun.isScheduled) return "scheduled";
    return campaignStatus ?? statusFromApi ?? "draft";
  }, [scheduledRun.isScheduled, campaignStatus, statusFromApi]);

  const blockedByEmptyLeads =
    !isLeadsCountLoading &&
    !hasLeads &&
    (effectiveCampaignStatus === "draft" ||
      effectiveCampaignStatus === "paused");

  const { mutate: campaignStatusAction } = useCampaignStatusAction({
    action: "start",
    campaignId: campaignId,
  });

  const campaignName =
    normalizedCampaign?.name || normalizedCampaign?.campaignName || "";
  const validTabs = CampaignTabs.some((campaignTab) => campaignTab.key === tab);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showStartOptionsModal, setShowStartOptionsModal] = useState(false);
  const [startMode, setStartMode] = useState("now");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledTimezone, setScheduledTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const { data: readiness, isLoading: isLoadingReadiness } =
    useGetCampaignReadiness(campaignId, showStartOptionsModal);
  const domainAuthenticationIssue = readiness?.issues?.find(
    (item) => item.code === "email_domain_authentication_incomplete",
  );

  const handleTabClick = (newTab) => {
    navigate(`/campaigns/${campaignId}/${newTab}`);
  };

  const handleCampaignStatusAction = () => {
    if (scheduledRun.isScheduled) return;

    const currentStatus = String(
      campaignStatus ?? statusFromApi ?? "draft",
    ).toLowerCase();

    if (currentStatus === "draft") {
      if (isLeadsCountLoading) {
        showError("Please wait while we check your campaign leads.");
        return;
      }
      if (!hasLeads) {
        showError("Add leads first, you can't run a campaign without leads.");
        return;
      }
      setShowStartOptionsModal(true);
      return;
    }

    const nextAction = resolveActionForStatus(currentStatus);
    if (!nextAction) return;

    if (
      (nextAction === "start" || nextAction === "resume") &&
      (isLeadsCountLoading || !hasLeads)
    ) {
      if (isLeadsCountLoading) {
        showError("Please wait while we check your campaign leads.");
        return;
      }
      showError("Add leads first, you can't run a campaign without leads.");
      return;
    }

    campaignStatusAction(
      { [CAMPAIGN_HTTP_ACTION_KEY]: nextAction },
      {
        onSuccess: () => {
          if (nextAction === "stop") {
            setCampaignStatus("paused");
          } else if (nextAction === "resume") {
            setCampaignStatus("active");
          }
        },
      },
    );
  };

  const handleConfirmStartCampaign = () => {
    if (isLeadsCountLoading) {
      showError("Please wait while we check your campaign leads.");
      return;
    }
    if (!hasLeads) {
      showError("Add leads first, you can't run a campaign without leads.");
      return;
    }
    const startPayload = {
      [CAMPAIGN_HTTP_ACTION_KEY]: "start",
    };
    const onStartSuccess = () => {
      setCampaignStatus("active");
      setShowStartOptionsModal(false);
    };

    if (startMode === "later") {
      if (!scheduledDate || !scheduledTime || !scheduledTimezone) {
        showError("Please choose date, time and timezone.");
        return;
      }
      campaignStatusAction(
        {
          ...startPayload,
          startMode: "schedule_later",
          scheduledAt: `${scheduledDate}T${scheduledTime}`,
          timezone: scheduledTimezone,
        },
        { onSuccess: onStartSuccess },
      );
    } else {
      campaignStatusAction(startPayload, { onSuccess: onStartSuccess });
    }
  };

  useEffect(() => {
    if (statusFromApi) {
      setCampaignStatus(statusFromApi);
    }
  }, [statusFromApi]);

  const handleOpenDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(true);
  };
  const handleDeleteCampaign = () => {
    deleteCampaign({ campaignId: campaignId });
    setShowDeleteConfirmModal(false);
    navigate("/campaigns");
  };
  const handleCloseDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(false);
  };

  const CampaignOptions = [
    // {
    //   id: "Share",
    //   name: "Share",
    //   icon: <Share size={16} color="#252B37" />,
    //   onClick: () => {
    //     console.log("Campaign 1 clicked");
    //   },
    // },
    // {
    //   id: "Download Analytics",
    //   name: "Download Analytics",
    //   icon: <Download size={16} color="#252B37" />,
    //   onClick: () => {
    //     console.log("Campaign 1 clicked");
    //   },
    // },
    {
      id: "Duplicate",
      name: "Duplicate Campaign",
      icon: <Copy size={16} className="text-gray-700 dark:text-gray-300" />,
      onClick: () => duplicateCampaign({ campaignId }),
    },
    {
      id: "Campaign Settings",
      name: "Campaign Settings",
      icon: <Settings size={16} className="text-gray-700 dark:text-gray-300" />,
      onClick: () => {
        navigate(`/campaigns/${campaignId}/settings`);
      },
      show: tab !== "settings",
    },
    {
      id: "Delete",
      name: "Delete",
      icon: <Trash size={16} color="#F04438" />,
      onClick: handleOpenDeleteConfirmModal,
    },
  ];

  if (!validTabs) {
    return <Navigate to={`/campaigns/${campaignId}/analytics`} replace />;
  }

  if (isLoadingCampaign) {
    return (
      <div className="flex items-center justify-center h-[calc(100dvh-155px)]">
        <Loader text="Loading campaign details..." />
      </div>
    );
  }

  const campaignStartPauseButton = (
    <SecondaryButton
      title={
        scheduledRun.isScheduled
          ? "Scheduled"
          : effectiveCampaignStatus === "draft"
            ? "Start Campaign"
            : effectiveCampaignStatus === "paused"
              ? "Resume Campaign"
              : effectiveCampaignStatus === "active" ||
                effectiveCampaignStatus === "running"
                ? "Pause Campaign"
                : "Start Campaign"
      }
      icon={
        scheduledRun.isScheduled ? (
          <CalendarClock
            size={16}
            className="text-gray-500"
            strokeWidth={1.75}
          />
        ) : effectiveCampaignStatus === "active" ||
          effectiveCampaignStatus === "running" ? (
          <Pause size={16} className="text-gray-700 dark:text-gray-300" />
        ) : (
          <Play size={16} color="#12B76A" />
        )
      }
      onClick={handleCampaignStatusAction}
      textColor="light"
      disabled={scheduledRun.isScheduled || blockedByEmptyLeads}
    />
  );

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center justify-between w-full pb-3">
        {/* breadcrumbs */}
        <div className="flex flex-col gap-1.5">
          <BreadCrumbs
            items={[
              { label: "Campaigns", href: "/campaigns" },
              { label: campaignName },

            ]}
          />
        </div>
        {/* campaign resume/pause buttons */}
        <div className="flex flex-wrap gap-2 w-fit max-w-full items-center justify-end mr-4">
          {(tab === "leads" ||
            tab === "individualsequences" ||
            tab === "campaignsteps") && (
              <>
                {scheduledRun.isScheduled ? (
                  <CustomCommonTooltip
                    className="cursor-pointer text-gray-500"
                    content={`This campaign is scheduled to start${scheduledRun.subtitle ? ` on ${scheduledRun.subtitle}` : ""
                      }. It will run automatically — run controls are unavailable until it starts.`}
                  >
                    <span className="inline-flex">
                      {campaignStartPauseButton}
                    </span>
                  </CustomCommonTooltip>
                ) : blockedByEmptyLeads ? (
                  <CustomCommonTooltip
                    className="cursor-pointer text-gray-500"
                    content="Add at least one lead before starting or resuming this campaign."
                  >
                    <span className="inline-flex">
                      {campaignStartPauseButton}
                    </span>
                  </CustomCommonTooltip>
                ) : (
                  campaignStartPauseButton
                )}
              </>
            )}

          <CommonDropdownMenu actions={CampaignOptions} />
        </div>
      </div>

      {/* tabs with tab content */}
      <div className="w-full">
        <div className="flex flex-col w-full">
          <UnderlineTabs
            tab={tab}
            handleTabClick={handleTabClick}
            tabs={CampaignTabs}
          />
          <div
            className={`${tab !== "campaignsteps" ? "px-4 pt-4" : "p-0"}  bg-white dark:bg-gray-900 w-full h-[calc(100dvh-111px)] overflow-y-auto scrollbar-hide`}
          >
            {/* Tab content */}
            {tab === "analytics" && (
              <AnalyticsContent
                CampaignName={campaignName}
                campaignStatus={effectiveCampaignStatus}
              />
            )}
            {tab === "campaignsteps" && (
              <StepsContent />
            )}
            {tab === "leads" && <CampaignLeadsContent />}
            {tab === "individualsequences" && <IndividualSequenceContent />}
            {tab === "settings" && (
              <SettingContent
                campaignId={campaignId}
                campaignData={normalizedCampaign ?? campaignData}
              />
            )}
          </div>
        </div>
      </div>
      {showDeleteConfirmModal && (
        <DeleteConfirmationModal
          title="Are you sure you want to delete this campaign?"
          description="Deleting this campaign will permanently erase your Sequence, leads, and history - This action can’t be undone."
          showConfirmDelete={showDeleteConfirmModal}
          onClose={handleCloseDeleteConfirmModal}
          onDelete={handleDeleteCampaign}
          deleteButtonText="Yes, delete campaign"
          cancelButtonText="I changed my mind"
          isDeleting={isDeleting}
        />
      )}
      <CampaignStartConfirmationModal
        showStartOptionsModal={showStartOptionsModal}
        setShowStartOptionsModal={setShowStartOptionsModal}
        startMode={startMode}
        setStartMode={setStartMode}
        scheduledDate={scheduledDate}
        setScheduledDate={setScheduledDate}
        scheduledTime={scheduledTime}
        setScheduledTime={setScheduledTime}
        scheduledTimezone={scheduledTimezone}
        setScheduledTimezone={setScheduledTimezone}
        handleConfirmStartCampaign={handleConfirmStartCampaign}
        domainAuthenticationIssue={domainAuthenticationIssue}
        isLoadingReadiness={isLoadingReadiness}
        onCompleteDomainSetup={() => navigate("/settings/deliverability")}
      />
    </div>
  );
}
